// src/components/alerts/AlertTrendChart.js
// --- UPDATE (Alert System AI) — NEW: 14-day alert volume, stacked by severity, with the usual
// day (mean of the previous 13 days) as a dashed line. Plain SVG — no chart library needed.
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, HStack, Text, useColorModeValue, useToken } from '@chakra-ui/react';

const ORDER = ['low', 'medium', 'high', 'critical']; // bottom → top

function AlertTrendChart({ trend, storm, height = 180 }) {
    const [hover, setHover] = useState(null);
    const [cCritical, cHigh, cMedium, cLow, cGrid, cText, cMean] = useToken('colors', ['red.400', 'orange.400', 'yellow.400', 'blue.300', 'gray.200', 'gray.500', 'purple.400']);
    const gridColor = useColorModeValue(cGrid, 'rgba(255,255,255,0.08)');
    const tipBg = useColorModeValue('white', 'gray.700');
    const colors = { critical: cCritical, high: cHigh, medium: cMedium, low: cLow };

    const data = useMemo(() => (Array.isArray(trend) ? trend : []), [trend]);
    if (!data.length) return <Text fontSize="sm" color="gray.500">No trend data yet.</Text>;

    const W = 640, H = height, padL = 28, padB = 22, padT = 8;
    const max = Math.max(1, ...data.map(d => d.count), storm?.mean || 0);
    const bw = (W - padL) / data.length;
    const y = (v) => H - padB - (v / max) * (H - padB - padT);
    const ticks = [0, Math.ceil(max / 2), max];

    return (
        <Box position="relative">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Alerts per day, last 14 days" onMouseLeave={() => setHover(null)}>
                {ticks.map(t => (
                    <g key={t}>
                        <line x1={padL} x2={W} y1={y(t)} y2={y(t)} stroke={gridColor} />
                        <text x={padL - 6} y={y(t) + 4} fontSize="10" textAnchor="end" fill={cText}>{t}</text>
                    </g>
                ))}
                {data.map((d, i) => {
                    let acc = 0;
                    const x = padL + i * bw + bw * 0.15;
                    const isToday = i === data.length - 1;
                    return (
                        <g key={d.day} onMouseEnter={() => setHover(i)} style={{ cursor: 'default' }}>
                            <rect x={padL + i * bw} y={padT} width={bw} height={H - padB - padT} fill="transparent" />
                            {ORDER.map(sev => {
                                const v = d[sev] || 0;
                                if (!v) return null;
                                const y0 = y(acc), y1 = y(acc + v);
                                acc += v;
                                return <rect key={sev} x={x} y={y1} width={bw * 0.7} height={Math.max(1, y0 - y1)} rx="2" fill={colors[sev]} opacity={hover === null || hover === i ? 1 : 0.45} />;
                            })}
                            {(i % 2 === 1 || isToday) && (
                                <text x={x + bw * 0.35} y={H - 6} fontSize="10" textAnchor="middle" fill={cText} fontWeight={isToday ? 700 : 400}>
                                    {isToday ? 'Today' : d.day.slice(5)}
                                </text>
                            )}
                        </g>
                    );
                })}
                {storm?.mean !== null && storm?.mean !== undefined && (
                    <g>
                        <line x1={padL} x2={W} y1={y(storm.mean)} y2={y(storm.mean)} stroke={cMean} strokeDasharray="5 4" strokeWidth="1.4" />
                        <text x={W - 4} y={y(storm.mean) - 4} fontSize="10" textAnchor="end" fill={cMean}>usual day {storm.mean}</text>
                    </g>
                )}
            </svg>
            {hover !== null && data[hover] && (
                <Box position="absolute" top={2} left={`${Math.min(70, (hover / data.length) * 100)}%`} bg={tipBg} boxShadow="lg" borderRadius="md" px={3} py={2} fontSize="xs" pointerEvents="none" zIndex={2}>
                    <Text fontWeight="700">{data[hover].day} · {data[hover].count} alerts</Text>
                    {ORDER.slice().reverse().map(s => data[hover][s] ? <Text key={s} color={colors[s]}>{s}: {data[hover][s]}</Text> : null)}
                </Box>
            )}
            <HStack spacing={4} mt={1} fontSize="xs" color="gray.500" flexWrap="wrap">
                {ORDER.slice().reverse().map(s => (
                    <HStack key={s} spacing={1}><Box w="10px" h="10px" borderRadius="2px" bg={colors[s]} /><Text>{s}</Text></HStack>
                ))}
                {storm?.storm && <Text color="red.400" fontWeight="700">Alert storm today (z = {storm.z ?? '∞'})</Text>}
            </HStack>
        </Box>
    );
}

AlertTrendChart.propTypes = { trend: PropTypes.array, storm: PropTypes.object, height: PropTypes.number };

export default React.memo(AlertTrendChart);
// --- END UPDATE
